import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { formatDateTime } from '../../../utils/dateUtils';

const ArticlePreviewModal = ({ article, isOpen, onClose, onEdit }) => {
  if (!isOpen || !article) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { 
        color: 'bg-success text-success-foreground', 
        icon: 'CheckCircle',
        label: 'Published' 
      },
      draft: { 
        color: 'bg-muted text-muted-foreground', 
        icon: 'Edit3',
        label: 'Draft' 
      },
      scheduled: { 
        color: 'bg-warning text-warning-foreground', 
        icon: 'Clock',
        label: 'Scheduled' 
      }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      news: { color: 'bg-accent text-accent-foreground', label: 'News' },
      events: { color: 'bg-secondary text-secondary-foreground', label: 'Events' },
      announcements: { color: 'bg-primary text-primary-foreground', label: 'Announcements' }
    };

    const config = categoryConfig?.[category] || categoryConfig?.news;

    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-400 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl bg-card border border-border rounded-lg shadow-elevation-3 animate-slide-up max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
            <div className="flex items-center space-x-4">
              <Icon name="Eye" size={24} className="text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">Article Preview</h2>
                <p className="text-sm text-muted-foreground">
                  Preview how this article will appear to readers
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(article)}
                iconName="Edit3"
                iconPosition="left"
                iconSize={16}
              >
                Edit Article
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
                iconSize={20}
              />
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Article Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {getCategoryBadge(article?.category)}
                  {getStatusBadge(article?.status)}
                </div>
                
                <div className="text-sm text-muted-foreground">
                  {formatDateTime(article?.publishedAt)}
                </div>
              </div>

              <h1 className="text-3xl font-bold text-foreground mb-4 leading-tight">
                {article?.title}
              </h1>

              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs text-primary-foreground font-medium">
                      {article?.author?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span>By {article?.author}</span>
                </div>
                
                <span>•</span>
                <span>{article?.readTime || '5'} min read</span>
                
                {article?.tags && article?.tags?.length > 0 && (
                  <>
                    <span>•</span>
                    <div className="flex items-center space-x-1">
                      {article?.tags?.slice(0, 3)?.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-muted rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Featured Image */}
            {article?.featuredImage && (
              <div className="px-6 py-4">
                <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
                  <Image
                    src={article?.featuredImage}
                    alt={article?.featuredImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                {article?.featuredImageCaption && (
                  <p className="text-sm text-muted-foreground mt-2 text-center italic">
                    {article?.featuredImageCaption}
                  </p>
                )}
              </div>
            )}

            {/* Article Content */}
            <div className="px-6 pb-6">
              {article?.excerpt && (
                <div className="mb-6 p-4 bg-muted/30 rounded-lg border-l-4 border-primary">
                  <p className="text-lg text-muted-foreground italic leading-relaxed">
                    {article?.excerpt}
                  </p>
                </div>
              )}

              <div className="prose prose-lg max-w-none">
                <div 
                  className="text-foreground leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article?.content }}
                />
              </div>

              {/* Article Footer */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {article?.tags && article?.tags?.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Tag" size={16} className="text-muted-foreground" />
                        <div className="flex flex-wrap gap-2">
                          {article?.tags?.map((tag, index) => (
                            <span 
                              key={index} 
                              className="px-2 py-1 bg-muted text-muted-foreground rounded text-sm"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={16} />
                      <span>{article?.views || 0} views</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="MessageCircle" size={16} />
                      <span>{article?.comments || 0} comments</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default ArticlePreviewModal;